/**
 * Universal Guided Card Setup Wizard
 * 
 * Step-by-step card deployment workflow.
 * Mobile-first, consistent across all card types.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Building2,
  MapPin,
  Layers,
  CreditCard,
  Settings,
  Save,
  Copy,
  Loader2,
  CheckCircle2,
  ClipboardCheck,
  AlertTriangle,
  Star,
  Droplets,
  Megaphone,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${API_BASE}/api/hotels-standalone`;
const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || API_BASE;

const STEPS = [
  { id: 1, label: 'Client', icon: Building2 },
  { id: 2, label: 'Zone', icon: MapPin },
  { id: 3, label: 'Section', icon: Layers },
  { id: 4, label: 'Card Type', icon: CreditCard },
  { id: 5, label: 'Settings', icon: Settings },
  { id: 6, label: 'Deploy', icon: Save },
];

const CARD_TYPES = [
  {
    value: 'retailer_alert',
    label: 'Retailer Alert',
    desc: 'In-store incident & hazard reporting',
    icon: AlertTriangle,
    color: 'text-orange-400',
    bg: 'bg-orange-500/10 border-orange-500/30',
    urlPrefix: 'retailer-alert',
  },
  {
    value: 'guest_arrival',
    label: 'Room Survey',
    desc: 'Guest arrival check-in surveys',
    icon: ClipboardCheck,
    color: 'text-green-400',
    bg: 'bg-green-500/10 border-green-500/30',
    urlPrefix: 'room',
  },
  {
    value: 'restroom',
    label: 'Restroom Survey',
    desc: 'Facility cleanliness feedback',
    icon: Droplets,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10 border-blue-500/30',
    urlPrefix: 'restroom',
  },
  {
    value: 'tap_engine',
    label: 'Review Engine',
    desc: 'External review collection (Google, TripAdvisor)',
    icon: Star,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10 border-amber-500/30',
    urlPrefix: 'review',
  },
];

export default function GuidedCardSetup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedOrg = searchParams.get('org');
  const preselectedType = searchParams.get('type');

  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  // Data
  const [organizations, setOrganizations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [selectedOrg, setSelectedOrg] = useState('');
  const [selectedOrgName, setSelectedOrgName] = useState('');
  const [zone, setZone] = useState('');
  const [section, setSection] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [cardId, setCardId] = useState('');
  const [autoId, setAutoId] = useState('');
  const [useAutoId, setUseAutoId] = useState(true);

  // Result
  const [createdCard, setCreatedCard] = useState(null);
  const [generatedUrl, setGeneratedUrl] = useState('');

  const token = localStorage.getItem('hotels_token') || localStorage.getItem('token');

  useEffect(() => {
    document.documentElement.classList.add('dark');
    if (!token) { navigate('/standalone/login'); return; }
    fetchOrganizations();
    return () => {
      const savedTheme = localStorage.getItem('yul_theme_preference');
      if (savedTheme !== 'dark') document.documentElement.classList.remove('dark');
    };
  }, []);

  useEffect(() => {
    if (preselectedOrg && organizations.length > 0) {
      const org = organizations.find(o => o.id === preselectedOrg);
      if (org) {
        setSelectedOrg(org.id);
        setSelectedOrgName(org.name);
        setStep(2);
      }
    }
  }, [preselectedOrg, organizations]);

  useEffect(() => {
    if (preselectedType && CARD_TYPES.find(t => t.value === preselectedType)) {
      setSelectedType(preselectedType);
    }
  }, [preselectedType]);

  useEffect(() => {
    if (selectedOrg) {
      fetchLocations();
      fetchNextCardId();
    }
  }, [selectedOrg]);

  const fetchOrganizations = async () => {
    try {
      const res = await fetch(`${API_URL}/organizations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setOrganizations(data);
      if (data.length === 1 && !preselectedOrg) {
        setSelectedOrg(data[0].id);
        setSelectedOrgName(data[0].name);
      }
    } catch { toast.error('Failed to load organizations'); }
    finally { setLoading(false); }
  };

  const fetchLocations = async () => {
    try {
      const res = await fetch(`${API_URL}/organizations/${selectedOrg}/locations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setLocations(await res.json());
    } catch {}
  };

  const fetchNextCardId = async () => {
    try {
      const res = await fetch(`${API_URL}/organizations/${selectedOrg}/next-card-id`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAutoId(data.next_card_id);
        if (useAutoId) setCardId(data.next_card_id);
      }
    } catch {}
  };

  const handleSelectOrg = (org) => {
    setSelectedOrg(org.id);
    setSelectedOrgName(org.name);
    setStep(2);
  };

  const handleSaveCard = async () => {
    const finalCardId = useAutoId ? autoId : cardId.trim();
    if (!finalCardId) { toast.error('Card ID is required'); return; }
    if (!selectedOrg) { toast.error('Please select an organization'); return; }
    if (!selectedType) { toast.error('Please select a card type'); return; }

    setSaving(true);
    try {
      // 1. Create the card
      const res = await fetch(`${API_URL}/organizations/${selectedOrg}/cards`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          card_id: finalCardId,
          engagement_type: selectedType,
          location_id: null,
          zone: zone || null,
          section: section || null,
        })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create card');
      }
      const cardData = await res.json();

      // 2. For retailer_alert, also create a location and assign the tag
      if (selectedType === 'retailer_alert') {
        const locName = section || zone || 'Location';
        const locRes = await fetch(`${API_URL}/organizations/${selectedOrg}/retailer-alert-locations`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: locName,
            floor: zone || '',
            building: '',
            zone_number: zone || '1',
          })
        });
        if (locRes.ok) {
          const locData = await locRes.json();
          // Assign tag to location
          await fetch(`${API_URL}/organizations/${selectedOrg}/retailer-alert-locations/${locData.id}/assign-tag`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ tag_id: finalCardId })
          });
          // Activate the card
          await fetch(`${API_URL}/cards/${finalCardId}/assign`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              engagement_type: selectedType,
              location_id: locData.id,
            })
          });
        }
      } else {
        // Activate other card types
        await fetch(`${API_URL}/cards/${finalCardId}/assign`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ engagement_type: selectedType })
        });
      }

      // 3. Build the URL
      const typeConfig = CARD_TYPES.find(t => t.value === selectedType);
      const url = `${PUBLIC_URL}/standalone/${typeConfig.urlPrefix}/${finalCardId}`;
      setGeneratedUrl(url);
      setCreatedCard(cardData);
      setSaved(true);
      toast.success('Card deployed successfully!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(generatedUrl);
    setCopied(true);
    toast.success('URL copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const canProceed = () => {
    switch (step) {
      case 1: return !!selectedOrg;
      case 2: return true; // Zone is optional
      case 3: return true; // Section is optional
      case 4: return !!selectedType;
      case 5: return !!(useAutoId ? autoId : cardId.trim());
      default: return true;
    }
  };

  const goNext = () => { if (canProceed() && step < 6) setStep(step + 1); };
  const goBack = () => { if (step > 1) setStep(step - 1); };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(217,19%,12%)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // SUCCESS SCREEN
  if (saved && createdCard) {
    const typeConfig = CARD_TYPES.find(t => t.value === selectedType);
    const TypeIcon = typeConfig?.icon || CreditCard;
    return (
      <div className="min-h-screen bg-[hsl(217,19%,12%)] p-4 flex flex-col items-center justify-center">
        <Card className="w-full max-w-lg bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)]">
          <CardContent className="pt-8 pb-8 text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-green-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white" data-testid="setup-success-title">Card Deployed</h2>
              <p className="text-sm text-[hsl(215,20%,55%)] mt-1">{selectedOrgName} &middot; {typeConfig?.label}</p>
            </div>

            <div className="space-y-3 text-left">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(217,19%,14%)] border border-[hsl(217,19%,24%)]">
                <TypeIcon className={`w-5 h-5 ${typeConfig?.color}`} />
                <div>
                  <p className="text-xs text-[hsl(215,20%,55%)]">Card ID</p>
                  <p className="text-white font-mono font-semibold" data-testid="setup-card-id">{createdCard.card_id}</p>
                </div>
              </div>
              {zone && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(217,19%,14%)] border border-[hsl(217,19%,24%)]">
                  <MapPin className="w-5 h-5 text-[hsl(215,20%,55%)]" />
                  <div>
                    <p className="text-xs text-[hsl(215,20%,55%)]">Zone</p>
                    <p className="text-white">{zone}</p>
                  </div>
                </div>
              )}
              {section && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(217,19%,14%)] border border-[hsl(217,19%,24%)]">
                  <Layers className="w-5 h-5 text-[hsl(215,20%,55%)]" />
                  <div>
                    <p className="text-xs text-[hsl(215,20%,55%)]">Section</p>
                    <p className="text-white">{section}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-[hsl(215,20%,55%)]">NFC / QR URL — Program this to your card</Label>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={generatedUrl}
                  className="bg-[hsl(217,19%,14%)] border-[hsl(217,19%,24%)] text-white font-mono text-xs"
                  data-testid="setup-generated-url"
                />
                <Button
                  size="sm"
                  variant={copied ? "default" : "outline"}
                  onClick={handleCopyUrl}
                  className="shrink-0"
                  data-testid="setup-copy-url-btn"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-2">
              <Button
                onClick={() => {
                  setSaved(false);
                  setCreatedCard(null);
                  setCardId('');
                  setZone('');
                  setSection('');
                  setSelectedType('');
                  setStep(2);
                  fetchNextCardId();
                }}
                className="w-full"
                data-testid="setup-deploy-another-btn"
              >
                Deploy Another Card
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/standalone/cards')}
                className="w-full"
                data-testid="setup-view-cards-btn"
              >
                View All Cards
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/standalone/dashboard')}
                className="w-full text-[hsl(215,20%,55%)]"
              >
                Back to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(217,19%,12%)] flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[hsl(217,19%,14%)] border-b border-[hsl(217,19%,24%)] px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <button
            onClick={() => step === 1 ? navigate(-1) : goBack()}
            className="flex items-center gap-1 text-sm text-[hsl(215,20%,65%)] hover:text-white transition-colors"
            data-testid="setup-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            {step === 1 ? 'Exit' : 'Back'}
          </button>
          <h1 className="text-white font-semibold text-sm">Setup Card</h1>
          <span className="text-xs text-[hsl(215,20%,55%)]">{step} / {STEPS.length}</span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="px-4 pt-4 pb-2">
        <div className="max-w-lg mx-auto">
          <div className="flex gap-1.5">
            {STEPS.map((s) => (
              <div
                key={s.id}
                className={`h-1.5 rounded-full flex-1 transition-all duration-300 ${
                  s.id <= step ? 'bg-primary' : 'bg-[hsl(217,19%,24%)]'
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2 mt-3">
            {React.createElement(STEPS[step - 1].icon, { className: 'w-4 h-4 text-primary' })}
            <span className="text-xs font-medium text-primary">{STEPS[step - 1].label}</span>
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="flex-1 px-4 pb-24">
        <div className="max-w-lg mx-auto">

          {/* STEP 1 — Select Client */}
          {step === 1 && (
            <div className="space-y-4" data-testid="setup-step-1">
              <div className="mb-4">
                <h2 className="text-lg font-bold text-white">Select Client</h2>
                <p className="text-sm text-[hsl(215,20%,55%)]">Choose the client or organization where this card will be installed.</p>
              </div>
              {organizations.map((org) => (
                <button
                  key={org.id}
                  onClick={() => handleSelectOrg(org)}
                  className={`w-full text-left p-4 rounded-xl border transition-all ${
                    selectedOrg === org.id
                      ? 'bg-primary/10 border-primary'
                      : 'bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] hover:border-[hsl(215,20%,40%)]'
                  }`}
                  data-testid={`setup-org-${org.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedOrg === org.id ? 'bg-primary/20' : 'bg-[hsl(217,19%,22%)]'}`}>
                      <Building2 className={`w-5 h-5 ${selectedOrg === org.id ? 'text-primary' : 'text-[hsl(215,20%,55%)]'}`} />
                    </div>
                    <div>
                      <p className="text-white font-medium">{org.name}</p>
                      <p className="text-xs text-[hsl(215,20%,55%)]">{org.hotel_code || org.id.slice(0,8)}</p>
                    </div>
                    {selectedOrg === org.id && <Check className="w-5 h-5 text-primary ml-auto" />}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* STEP 2 — Zone */}
          {step === 2 && (
            <div className="space-y-4" data-testid="setup-step-2">
              <div className="mb-4">
                <h2 className="text-lg font-bold text-white">Select Zone</h2>
                <p className="text-sm text-[hsl(215,20%,55%)]">Select the zone or area of the facility. Example: Floor 1, Building A, Entrance.</p>
              </div>
              <Input
                placeholder="e.g., Floor 1, Building A, Main Area"
                value={zone}
                onChange={(e) => setZone(e.target.value)}
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white text-base h-12"
                data-testid="setup-zone-input"
                autoFocus
              />
              <p className="text-xs text-[hsl(215,20%,45%)]">Optional — helps organize where the card is physically located.</p>
            </div>
          )}

          {/* STEP 3 — Section */}
          {step === 3 && (
            <div className="space-y-4" data-testid="setup-step-3">
              <div className="mb-4">
                <h2 className="text-lg font-bold text-white">Select Section</h2>
                <p className="text-sm text-[hsl(215,20%,55%)]">Choose the specific location within the zone. Example: Lobby, Aisle 3, Room 101.</p>
              </div>
              <Input
                placeholder="e.g., Main Lobby, Aisle 3, Entrance A"
                value={section}
                onChange={(e) => setSection(e.target.value)}
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white text-base h-12"
                data-testid="setup-section-input"
                autoFocus
              />
              {locations.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-[hsl(215,20%,45%)]">Or pick an existing location:</p>
                  <div className="flex flex-wrap gap-2">
                    {locations.map((loc) => (
                      <button
                        key={loc.id}
                        onClick={() => setSection(loc.name)}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                          section === loc.name
                            ? 'bg-primary/20 border-primary text-primary'
                            : 'bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)] hover:border-[hsl(215,20%,40%)]'
                        }`}
                      >
                        {loc.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-xs text-[hsl(215,20%,45%)]">Optional — identifies the exact spot for this card.</p>
            </div>
          )}

          {/* STEP 4 — Card Type */}
          {step === 4 && (
            <div className="space-y-4" data-testid="setup-step-4">
              <div className="mb-4">
                <h2 className="text-lg font-bold text-white">Select Card Type</h2>
                <p className="text-sm text-[hsl(215,20%,55%)]">Select the type of card you want to deploy.</p>
              </div>
              {CARD_TYPES.map((type) => {
                const Icon = type.icon;
                const isSelected = selectedType === type.value;
                return (
                  <button
                    key={type.value}
                    onClick={() => setSelectedType(type.value)}
                    className={`w-full text-left p-4 rounded-xl border transition-all ${
                      isSelected ? type.bg : 'bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] hover:border-[hsl(215,20%,40%)]'
                    }`}
                    data-testid={`setup-type-${type.value}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSelected ? 'bg-white/10' : 'bg-[hsl(217,19%,22%)]'}`}>
                        <Icon className={`w-5 h-5 ${isSelected ? type.color : 'text-[hsl(215,20%,55%)]'}`} />
                      </div>
                      <div className="flex-1">
                        <p className={`font-medium ${isSelected ? 'text-white' : 'text-[hsl(215,20%,75%)]'}`}>{type.label}</p>
                        <p className="text-xs text-[hsl(215,20%,50%)]">{type.desc}</p>
                      </div>
                      {isSelected && <Check className={`w-5 h-5 ${type.color}`} />}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* STEP 5 — Configure */}
          {step === 5 && (
            <div className="space-y-4" data-testid="setup-step-5">
              <div className="mb-4">
                <h2 className="text-lg font-bold text-white">Configure Card</h2>
                <p className="text-sm text-[hsl(215,20%,55%)]">Adjust any options specific to this card.</p>
              </div>

              {/* Summary so far */}
              <div className="p-3 rounded-lg bg-[hsl(217,19%,15%)] border border-[hsl(217,19%,24%)] space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-[hsl(215,20%,50%)]">Client</span>
                  <span className="text-white">{selectedOrgName}</span>
                </div>
                {zone && (
                  <div className="flex justify-between text-xs">
                    <span className="text-[hsl(215,20%,50%)]">Zone</span>
                    <span className="text-white">{zone}</span>
                  </div>
                )}
                {section && (
                  <div className="flex justify-between text-xs">
                    <span className="text-[hsl(215,20%,50%)]">Section</span>
                    <span className="text-white">{section}</span>
                  </div>
                )}
                <div className="flex justify-between text-xs">
                  <span className="text-[hsl(215,20%,50%)]">Type</span>
                  <span className="text-white">{CARD_TYPES.find(t => t.value === selectedType)?.label}</span>
                </div>
              </div>

              {/* Card ID */}
              <div className="space-y-3">
                <Label className="text-white">Card ID</Label>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setUseAutoId(true); setCardId(autoId); }}
                    className={`flex-1 p-3 rounded-lg border text-sm text-center transition-all ${
                      useAutoId
                        ? 'bg-primary/10 border-primary text-primary'
                        : 'bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)]'
                    }`}
                    data-testid="setup-auto-id-btn"
                  >
                    Auto: {autoId}
                  </button>
                  <button
                    onClick={() => { setUseAutoId(false); setCardId(''); }}
                    className={`flex-1 p-3 rounded-lg border text-sm text-center transition-all ${
                      !useAutoId
                        ? 'bg-primary/10 border-primary text-primary'
                        : 'bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-[hsl(215,20%,65%)]'
                    }`}
                    data-testid="setup-custom-id-btn"
                  >
                    Custom ID
                  </button>
                </div>
                {!useAutoId && (
                  <Input
                    placeholder="Enter custom card ID"
                    value={cardId}
                    onChange={(e) => setCardId(e.target.value)}
                    className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white font-mono text-base h-12"
                    data-testid="setup-custom-id-input"
                    autoFocus
                  />
                )}
              </div>

              {/* URL Preview */}
              <div className="space-y-1">
                <Label className="text-xs text-[hsl(215,20%,50%)]">URL Preview</Label>
                <p className="text-xs font-mono text-[hsl(215,20%,65%)] bg-[hsl(217,19%,14%)] p-2 rounded border border-[hsl(217,19%,24%)] break-all" data-testid="setup-url-preview">
                  {PUBLIC_URL}/standalone/{CARD_TYPES.find(t => t.value === selectedType)?.urlPrefix}/{useAutoId ? autoId : (cardId || '...')}
                </p>
              </div>
            </div>
          )}

          {/* STEP 6 — Deploy */}
          {step === 6 && (
            <div className="space-y-4" data-testid="setup-step-6">
              <div className="mb-4">
                <h2 className="text-lg font-bold text-white">Ready to Deploy</h2>
                <p className="text-sm text-[hsl(215,20%,55%)]">Review your card setup and tap Save to deploy.</p>
              </div>

              {/* Full Summary */}
              <Card className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)]">
                <CardContent className="pt-4 space-y-3">
                  {[
                    { label: 'Client', value: selectedOrgName },
                    { label: 'Zone', value: zone || '—' },
                    { label: 'Section', value: section || '—' },
                    { label: 'Card Type', value: CARD_TYPES.find(t => t.value === selectedType)?.label },
                    { label: 'Card ID', value: useAutoId ? autoId : cardId, mono: true },
                  ].map((item, i) => (
                    <div key={i} className="flex justify-between items-center py-1.5 border-b border-[hsl(217,19%,22%)] last:border-0">
                      <span className="text-sm text-[hsl(215,20%,55%)]">{item.label}</span>
                      <span className={`text-sm text-white ${item.mono ? 'font-mono font-semibold' : ''}`}>{item.value}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <p className="text-xs text-[hsl(215,20%,45%)] text-center">
                Tapping Save Card will create the card, assign the zone, generate the URL, and log the deployment.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-[hsl(217,19%,14%)] border-t border-[hsl(217,19%,24%)] p-4">
        <div className="max-w-lg mx-auto">
          {step < 6 ? (
            <Button
              onClick={goNext}
              disabled={!canProceed()}
              className="w-full h-12 text-base"
              data-testid="setup-next-btn"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSaveCard}
              disabled={saving}
              className="w-full h-12 text-base bg-green-600 hover:bg-green-700"
              data-testid="setup-save-btn"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deploying...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save Card</>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
